import {
  ChangeControlValueMeta,
  Control,
  ControlType,
  ControlWithinTree,
  ValueEmittedFromControlInputDecoratedWithMetadata
} from "@/types/controls";
import {
  DocumentStorePropertyChange,
  GenericStorePropertyChange,
  PresetStorePropertyChange,
  SelectorStorePropertyChange,
  SettingStorePropertyChange,
  StorePropertyChangeMetadata
} from "@/types/store-property-change";
import lodash, { uniq } from "lodash";
import {
  generateGenericStorePropertyChangeSnapshotLabelBasingOnChangedControl,
  getPropertyChangeSnapshotLabelAction
} from "@/lib/undo/snapshot-labels/generic-store-property-change.utils";
import { getBuilderElementNameByTreeNodeId } from "@/lib/undo/snapshot-labels/document-store-property-change.utils";
import { SnapshotLabelActionAndSubject } from "@/types/snapshot";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { GradientColorV } from "@/types/colors";
import { configStore, uiStore } from "@/store/store-accessor";
import { isControlValueEmpty } from "@/lib/controls/values";

export function addHoverToPath(path: string): string {
  return `${path}_hover`;
}

export function findControlAncestors(
  controlWithinTree: ControlWithinTree
): Control[] | undefined {
  for (const controlsTreeNode of controlWithinTree.controlsTree) {
    const stack = [controlsTreeNode];
    if (controlsTreeNode.children.includes(controlWithinTree.control)) {
      return stack;
    } else {
      const ancestors = findControlAncestors({
        control: controlWithinTree.control,
        controlsTree: controlsTreeNode.children
      });

      if (ancestors) {
        return [...stack, ...ancestors];
      }
    }
  }

  return undefined;
}

export function addMetadataToDocumentPropertyChangeBasingOnControlValueChangeMetadata(
  propertyChangeWithoutMetadata: Omit<DocumentStorePropertyChange, "meta">,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): DocumentStorePropertyChange {
  return addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata(
    getBuilderElementNameByTreeNodeId(propertyChangeWithoutMetadata.elementId),
    propertyChangeWithoutMetadata,
    changedControlWithinTree,
    controlValueChangeMetadata
  );
}

export function addMetadataToSelectorPropertyChangeBasingOnControlValueChangeMetadata(
  propertyChangeWithoutMetadata: Omit<SelectorStorePropertyChange, "meta">,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): SelectorStorePropertyChange {
  return addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata(
    "Selectors",
    propertyChangeWithoutMetadata,
    changedControlWithinTree,
    controlValueChangeMetadata
  );
}

export function addMetadaToVariablePropertyChangeBasingOnControlValueChangeMetadata(
  propertyChangeWithoutMetadata: Omit<SelectorStorePropertyChange, "meta">,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): SelectorStorePropertyChange {
  return addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata(
    "Variables",
    propertyChangeWithoutMetadata,
    changedControlWithinTree,
    controlValueChangeMetadata
  );
}

export function addMetadataToPresetPropertyChangeBasingOnControlValueChangeMetadata(
  propertyChangeWithoutMetadata: Omit<PresetStorePropertyChange, "meta">,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): PresetStorePropertyChange {
  return addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata(
    "Presets",
    propertyChangeWithoutMetadata,
    changedControlWithinTree,
    controlValueChangeMetadata
  );
}

export function addMetadataToSettingPropertyChangeBasingOnControlValueChangeMetadata(
  propertyChangeWithoutMetadata: Omit<SettingStorePropertyChange, "meta">,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): SettingStorePropertyChange {
  return addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata(
    "Global Settings",
    propertyChangeWithoutMetadata,
    changedControlWithinTree,
    controlValueChangeMetadata
  );
}

export function addMetadataToGenericPropertyChangeBasingOnControlValueChangeMetadata<
  T extends Omit<GenericStorePropertyChange, "meta">
>(
  snapshotLabelActionSubject: SnapshotLabelActionAndSubject["subject"],
  propertyChangeWithoutMetadata: T,
  changedControlWithinTree: ControlWithinTree,
  controlValueChangeMetadata?: ChangeControlValueMeta
): T & { meta: StorePropertyChangeMetadata } {
  const action = getPropertyChangeSnapshotLabelAction(
    propertyChangeWithoutMetadata
  );

  const transactionBundlingKeySuffixParts: string[] = [action];

  if (
    // TODO in theory there may be multiple levels of nested controls, only one is handled as of now
    controlValueChangeMetadata?.nestedControlThatWasChanged &&
    !controlValueChangeMetadata.nestedControlThatWasChanged.meta
      ?.disableTransactionBundling
  ) {
    transactionBundlingKeySuffixParts.push(
      controlValueChangeMetadata.nestedControlThatWasChanged.path
    );
  }

  const transactionBundlingOptionsWhenItsEnabled =
    transactionBundlingKeySuffixParts.length
      ? { bundlingKeySuffix: transactionBundlingKeySuffixParts.join(":") }
      : undefined;

  return {
    ...propertyChangeWithoutMetadata,
    meta: {
      snapshotLabel:
        generateGenericStorePropertyChangeSnapshotLabelBasingOnChangedControl(
          { action, subject: snapshotLabelActionSubject },
          changedControlWithinTree,
          controlValueChangeMetadata?.nestedControlThatWasChanged
        ),
      transactionBundling:
        controlValueChangeMetadata?.disableTransactionBundling
          ? { disable: true }
          : transactionBundlingOptionsWhenItsEnabled
    }
  };
}

export function getControlLabelWithAncestorsLabelsFirst(
  controlWithinTree: ControlWithinTree
): string[] {
  const controlAncestors = findControlAncestors(controlWithinTree) || [];

  return uniq(
    [...controlAncestors, controlWithinTree.control].map(
      control => control.label
    )
  );
}

export function unwrapControlValue<T>(
  value: ValueEmittedFromControlInputDecoratedWithMetadata<T> | unknown
) {
  if (value instanceof ValueEmittedFromControlInputDecoratedWithMetadata) {
    return value.value;
  } else {
    return value;
  }
}

export function getBlankControl(): Control {
  return {
    slug: "new_control",
    label: "New Control",
    keywords: [],
    options: {
      type: "text",
      layout: "vertical"
    },
    enableMediaQueries: false,
    enableHover: false,
    enableVariables: false,
    children: []
  };
}

export function getBlankControlSection(): Control {
  return {
    slug: "new_section",
    label: "New Section",
    keywords: [],
    options: {
      type: "section",
      sectionOptions: {
        type: "popout"
      }
    },
    enableMediaQueries: false,
    enableHover: false,
    enableVariables: false,
    children: []
  };
}

export function getPreviousBreakpointValue(
  type: ControlType,
  breakpointValues: Record<string, unknown> | unknown
) {
  if (!uiStore.settings.showPreviousBreakpointValueAsPlaceholder) {
    return undefined;
  }

  const breakpoints = configStore.breakpointIds;
  const currentBreakpointIndex = breakpoints.indexOf(
    uiStore.currentBreakpoint.id
  );

  const values = breakpoints
    .slice(0, currentBreakpointIndex)
    .map(breakpoint => lodash.get(breakpointValues, breakpoint));

  if (type === "spacing_complex") {
    const mergedValues = lodash.merge({}, ...values);
    return controlValueToPlaceholder(type, mergedValues);
  }

  // Get the first non-empty value that comes before the current breakpoint
  const value = values.reverse().find(value => !isControlValueEmpty(value));

  return controlValueToPlaceholder(type, value);
}

export function controlValueToPlaceholder(
  type: ControlType,
  value: unknown
): unknown {
  if (value === null || value === undefined) return "";

  if (type === "unit") {
    return value;
  }

  if (type === "font_family") {
    const foundVariable = configStore.fonts.find(font => font.slug === value);
    return foundVariable?.label || "";
  }

  if (type === "color") {
    return pipe(
      value,
      GradientColorV.decode,
      fold(
        () => value,
        gradient => gradient.type
      )
    );
  }

  if (type === "spacing_complex") {
    const spacing = value as Record<string, unknown>;

    return {
      left: controlValueToPlaceholder("unit", spacing?.left),
      right: controlValueToPlaceholder("unit", spacing?.right),
      top: controlValueToPlaceholder("unit", spacing?.top),
      bottom: controlValueToPlaceholder("unit", spacing?.bottom)
    };
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}
